import { useMemo, useState } from 'react';
import { Box, CircularProgress, LinearProgress, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useArbeitspakete } from '../../../api/hooks/useArbeitspakete';
import { useProjectTasks, useCreateTask, useUpdateTask, useDeleteTask } from '../../../api/hooks/useProjects';
import { useMeetings, useCreateMeeting, useUpdateMeeting, useDeleteMeeting } from '../../../api/hooks/useMeetings';
import { useNotifications } from '../../../context/NotificationContext';
import { formatApiError } from '../../../api/client';
import { PmInfoBox, PmCard, PmPill } from '../../../components/pm';
import { toApiDatetime } from '../lib/taskDate';
import { combineDateTimeToIso } from '../lib/dateTimeIso';
import { apLabel } from '../lib/apLabel';
import { buildApTree, apParentIds, collapsedHiddenIds, type ApNumberNode } from '../lib/apNumbering';
import { ApTaskGantt, type GanttTask, type ApMeeting, type NewTaskFields, type NewMeetingFields } from './ApTaskGantt';
import { TaskEditDialog, type EditableTask, type TaskEditValues } from './TaskEditDialog';
import { MeetingEditDialog, type EditableMeeting, type MeetingEditValues } from './MeetingEditDialog';

interface ApRow {
  id: string;
  nr: string;
  parent_id: string | null;
  titel: string | null;
  start_date: string | null;
  end_date: string | null;
  status?: string | null;
  progress?: number | null;
  beschreibung?: string | null;
  leistungsbeschreibung?: string | null;
  dod?: string | null;
}

interface TaskRow extends GanttTask {
  ap_id: string | null;
}

interface MeetingRow extends ApMeeting {
  ap_id: string | null;
}

const STATUS_TONE: Record<string, 'neutral' | 'info' | 'success' | 'error'> = {
  'Offen': 'neutral',
  'In Arbeit': 'info',
  'Erledigt': 'success',
  'Blockiert': 'error',
};

/** Kopf-Infos eines Arbeitspakets fuer die Detailansicht. */
function ApInfoHeader({ ap }: { ap: ApRow }) {
  const { t } = useTranslation('projects');
  const scheduled = ap.start_date && ap.end_date;
  const sections: Array<{ label: string; value: string | null | undefined }> = [
    { label: t('arbeitspakete.leistungsbeschreibung', 'Leistungsbeschreibung'), value: ap.leistungsbeschreibung },
    { label: t('arbeitspakete.dod', 'Definition of Done'), value: ap.dod },
    { label: t('fields.description', 'Beschreibung'), value: ap.beschreibung },
  ].filter((s) => s.value && s.value.trim().length > 0);
  return (
    <PmCard>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>{apLabel(ap.nr)}</Typography>
        <Typography variant="h6">{ap.titel ?? '—'}</Typography>
        {scheduled && (
          <PmPill tone="neutral">
            {dayjs(ap.start_date).format('DD.MM.')} – {dayjs(ap.end_date).format('DD.MM.YYYY')}
          </PmPill>
        )}
        {ap.status && <PmPill tone={STATUS_TONE[ap.status] ?? 'neutral'}>{ap.status}</PmPill>}
      </Stack>
      {ap.progress != null && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5, maxWidth: 360 }}>
          <LinearProgress
            variant="determinate" value={Math.min(100, Math.max(0, ap.progress))}
            sx={{ flex: 1, height: 6, borderRadius: 3, background: 'var(--pm-bg-deep)',
              '& .MuiLinearProgress-bar': { background: 'var(--rittec-petrol)' } }}
          />
          <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'var(--pm-fg-muted)' }}>{ap.progress}%</Typography>
        </Stack>
      )}
      {sections.map((s) => (
        <Box key={s.label} sx={{ mt: 1.5 }}>
          <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)', fontWeight: 600, display: 'block' }}>{s.label}</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{s.value}</Typography>
        </Box>
      ))}
    </PmCard>
  );
}

/**
 * Projektplan-Bereich: pro terminiertem Arbeitspaket ein Mini-Gantt mit seinen
 * Tasks (Balken) und Meetings (Rauten). Aufgaben/Meetings werden im Gantt
 * angelegt (ap_id-Zuordnung), Tasks per Drag terminiert, beide per Dialog
 * bearbeitet/geloescht.
 *
 * - ohne focusApId: alle terminierten APs (Landing).
 * - mit focusApId: nur dieses eine AP (Kopf-Infos + Gantt bzw. Hinweis). Quelle:
 *   Sidebar-Subpunkt.
 */
export function ApTaskGanttSection({ projectId, focusApId }: { projectId: string; focusApId?: string }) {
  const { t } = useTranslation('projects');
  const { notify } = useNotifications();

  const { data: apResp, isLoading: apLoading } = useArbeitspakete(projectId);
  const { data: taskResp, isLoading: taskLoading } = useProjectTasks(projectId);
  const { data: meetingResp, isLoading: meetingLoading } = useMeetings(projectId);
  const createTask = useCreateTask();
  const updateTask = useUpdateTask();
  const deleteTask = useDeleteTask();
  const createMeeting = useCreateMeeting(projectId);
  const updateMeeting = useUpdateMeeting(projectId);
  const deleteMeeting = useDeleteMeeting(projectId);

  const [editTask, setEditTask] = useState<EditableTask | null>(null);
  const [editMeeting, setEditMeeting] = useState<EditableMeeting | null>(null);

  const allAps = useMemo(
    () => ((apResp as unknown as { data?: ApRow[] } | undefined)?.data ?? []),
    [apResp],
  );
  // Nur APs mit gesetztem Zeitraum bekommen in der Landing ein Gantt.
  const scheduledAps = useMemo(
    () => allAps.filter((ap) => ap.start_date && ap.end_date) as Array<ApRow & { start_date: string; end_date: string }>,
    [allAps],
  );

  // Hierarchie auf-/zuklappen: zugeklappte APs blenden ihre untergeordneten Gantt-Blöcke aus.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const parentIds = useMemo(() => apParentIds(scheduledAps as ApNumberNode[]), [scheduledAps]);
  const hiddenIds = useMemo(() => collapsedHiddenIds(scheduledAps as ApNumberNode[], collapsed), [scheduledAps, collapsed]);
  const orderedScheduled = useMemo(
    () => buildApTree(scheduledAps).filter((t) => !hiddenIds.has(t.node.id)),
    [scheduledAps, hiddenIds],
  );
  const toggleCollapse = (id: string) => setCollapsed((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const tasksByAp = useMemo(() => {
    const raw = (taskResp as unknown as { data?: TaskRow[] } | undefined)?.data ?? [];
    const map = new Map<string, GanttTask[]>();
    for (const task of raw) {
      if (!task.ap_id) continue;
      const list = map.get(task.ap_id) ?? [];
      list.push({ id: task.id, name: task.name, abschluss_start: task.abschluss_start, abschluss_end: task.abschluss_end, state: task.state });
      map.set(task.ap_id, list);
    }
    return map;
  }, [taskResp]);

  const meetingsByAp = useMemo(() => {
    const raw = (meetingResp as unknown as { data?: MeetingRow[] } | undefined)?.data ?? [];
    const map = new Map<string, ApMeeting[]>();
    for (const m of raw) {
      if (!m.ap_id) continue;
      const list = map.get(m.ap_id) ?? [];
      list.push({ id: m.id, titel: m.titel, typ: m.typ, datum: m.datum });
      map.set(m.ap_id, list);
    }
    return map;
  }, [meetingResp]);

  const handleCreate = async (apId: string, fields: NewTaskFields) => {
    try {
      await createTask.mutateAsync({
        projectId,
        data: {
          name: fields.name,
          ap_id: apId,
          // pm-api validiert die Datumsfelder als ISO-8601-datetime, nicht als reines Datum.
          abschluss_start: toApiDatetime(fields.abschluss_start),
          abschluss_end: toApiDatetime(fields.abschluss_end),
        },
      });
      notify(t('tasks.created', 'Aufgabe angelegt'), 'success');
    } catch (err) {
      notify(formatApiError(err), 'error');
    }
  };

  const handleUpdateSchedule = async (taskId: string, start: string, end: string) => {
    try {
      await updateTask.mutateAsync({ projectId, taskId, data: { abschluss_start: toApiDatetime(start), abschluss_end: toApiDatetime(end) } });
    } catch (err) {
      notify(formatApiError(err), 'error');
    }
  };

  const handleSaveEdit = async (taskId: string, values: TaskEditValues) => {
    try {
      await updateTask.mutateAsync({
        projectId, taskId,
        data: { name: values.name, state: values.state, abschluss_start: toApiDatetime(values.abschluss_start), abschluss_end: toApiDatetime(values.abschluss_end) },
      });
      notify(t('tasks.saved', 'Aufgabe gespeichert'), 'success');
    } catch (err) {
      notify(formatApiError(err), 'error');
    }
  };

  const handleDelete = async (taskId: string) => {
    try {
      await deleteTask.mutateAsync({ projectId, taskId });
      notify(t('tasks.deleted', 'Aufgabe gelöscht'), 'success');
    } catch (err) {
      notify(formatApiError(err), 'error');
    }
  };

  const handleCreateMeeting = async (apId: string, fields: NewMeetingFields) => {
    try {
      await createMeeting.mutateAsync({
        titel: fields.titel || null,
        typ: fields.typ,
        datum: combineDateTimeToIso(fields.date, fields.time),
        ap_id: apId,
      });
      notify(t('meetings.created', 'Meeting angelegt'), 'success');
    } catch (err) {
      notify(formatApiError(err), 'error');
    }
  };

  const handleSaveMeeting = async (meetingId: string, values: MeetingEditValues) => {
    try {
      await updateMeeting.mutateAsync({
        id: meetingId,
        data: { titel: values.titel || null, typ: values.typ, datum: combineDateTimeToIso(values.date, values.time) },
      });
      notify(t('meetings.saved', 'Meeting gespeichert'), 'success');
    } catch (err) {
      notify(formatApiError(err), 'error');
    }
  };

  const handleDeleteMeeting = async (meetingId: string) => {
    try {
      await deleteMeeting.mutateAsync(meetingId);
      notify(t('meetings.deleted', 'Meeting gelöscht'), 'success');
    } catch (err) {
      notify(formatApiError(err), 'error');
    }
  };

  const openEdit = (task: GanttTask) => setEditTask({
    id: task.id, name: task.name,
    abschluss_start: task.abschluss_start, abschluss_end: task.abschluss_end,
    state: task.state ?? 'PLANNED',
  });
  const openEditMeeting = (m: ApMeeting) => setEditMeeting({ id: m.id, titel: m.titel, typ: m.typ, datum: m.datum });

  const renderGantt = (ap: ApRow & { start_date: string; end_date: string }) => (
    <ApTaskGantt
      key={ap.id}
      ap={ap}
      tasks={tasksByAp.get(ap.id) ?? []}
      meetings={meetingsByAp.get(ap.id) ?? []}
      onCreateTask={(fields) => handleCreate(ap.id, fields)}
      onUpdateSchedule={handleUpdateSchedule}
      onEditTask={openEdit}
      onCreateMeeting={(fields) => handleCreateMeeting(ap.id, fields)}
      onEditMeeting={openEditMeeting}
    />
  );

  const dialogs = (
    <>
      <TaskEditDialog open={editTask != null} task={editTask} onSave={handleSaveEdit} onDelete={handleDelete} onClose={() => setEditTask(null)} />
      <MeetingEditDialog open={editMeeting != null} meeting={editMeeting} onSave={handleSaveMeeting} onDelete={handleDeleteMeeting} onClose={() => setEditMeeting(null)} />
    </>
  );

  if (apLoading || taskLoading || meetingLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;
  }

  // --- Detailansicht: ein einzelnes AP (Sidebar-Subpunkt) ---
  if (focusApId) {
    const ap = allAps.find((a) => a.id === focusApId);
    if (!ap) {
      return <PmInfoBox>{t('tasks.apNotFound', 'Arbeitspaket nicht gefunden.')}</PmInfoBox>;
    }
    const scheduled = ap.start_date && ap.end_date;
    return (
      <Stack spacing={2}>
        <ApInfoHeader ap={ap} />
        {scheduled
          ? renderGantt(ap as ApRow & { start_date: string; end_date: string })
          : (
            <PmInfoBox>
              {t('tasks.apNoSchedule', 'Dieses Arbeitspaket hat keinen Zeitraum. Setze Start und Ende im Arbeitspakete-Tab, um Aufgaben im Zeitplan zu sehen.')}
            </PmInfoBox>
          )}
        {dialogs}
      </Stack>
    );
  }

  // --- Landing: alle terminierten APs ---
  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        {t('tasks.apGanttTitle', 'Aufgaben je Arbeitspaket')}
      </Typography>
      <Typography variant="caption" sx={{ color: 'var(--pm-fg-muted)', display: 'block', mb: 2 }}>
        {t('tasks.apGanttHint', 'Pro Arbeitspaket ein Zeitplan; „Aufgabe"/„Meeting" legt an, Balken ziehen terminiert (Kanten = Start/Ende), Stift/Raute bearbeitet.')}
      </Typography>

      {scheduledAps.length === 0 ? (
        <PmInfoBox>
          {t('tasks.noScheduledAps', 'Noch kein Arbeitspaket mit Zeitraum. Setze im Zeitplan Start und Ende eines Arbeitspakets, um Aufgaben zu planen.')}
        </PmInfoBox>
      ) : (
        <Stack spacing={2}>
          {orderedScheduled.map(({ node: ap, depth }) => (
            <Box key={ap.id} sx={{ ml: depth * 3 }}>
              {parentIds.has(ap.id) && (
                <Box
                  role="button"
                  aria-label={collapsed.has(ap.id) ? t('arbeitspakete.expand', 'Aufklappen') : t('arbeitspakete.collapse', 'Zuklappen')}
                  onClick={() => toggleCollapse(ap.id)}
                  sx={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 0.5, mb: 0.5, color: 'var(--pm-fg-subtle)', userSelect: 'none' }}
                >
                  <Box component="span" sx={{ fontSize: '0.8rem', lineHeight: 1 }}>{collapsed.has(ap.id) ? '▸' : '▾'}</Box>
                  <Typography variant="caption">
                    {collapsed.has(ap.id)
                      ? t('tasks.showSubAps', 'Unterpakete einblenden')
                      : t('tasks.hideSubAps', 'Unterpakete ausblenden')}
                  </Typography>
                </Box>
              )}
              {renderGantt(ap)}
            </Box>
          ))}
        </Stack>
      )}
      {dialogs}
    </Box>
  );
}
